import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Table,
  Input,
  InputNumber,
  Button,
  Select,
  Space,
  message,
  Typography,
  Flex,
  Tag,
  Badge,
  Alert,
  Empty,
  Modal,
  Form
} from 'antd';
import { SaveOutlined, ReloadOutlined, DashboardOutlined, TeamOutlined, TrophyOutlined, SwapOutlined, UserDeleteOutlined, PlusOutlined } from '@ant-design/icons';
import apiService from '@/services/api.service';
import TournamentService from '@/services/tournament.service';
import ClubService from '@/services/club.service';
import { CreateClubModal } from '@/components/admin/CreateClubModal';
import { logger } from '@/utils/logger';
import { sortTournamentCategories } from '@/utils/tournamentCategorySort';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { Option } = Select;

interface Participant {
  id: number;
  participant: {
    id: number;
    firstName: string;
    lastName: string;
    middleName?: string;
    weight?: number;
    gender: string;
    clubId: number;
    club?: {
      id: number;
      name: string;
    };
  };
  category?: {
    id: number;
    name: string;
    weightFrom?: number;
    weightTo?: number;
  };
}

interface Club {
  id: number;
  name: string;
}

interface TournamentCategory {
  id: number;
  categoryId?: number;
  name?: string;
  weightFrom?: number;
  weightTo?: number;
  weightCategory?: { id: number; name: string };
}

interface WeighInPageProps {
  tournamentId?: number;
}

const WeighInPage: React.FC<WeighInPageProps> = ({ tournamentId: propTournamentId }) => {
  const { id } = useParams<{ id: string }>();
  const tournamentId = propTournamentId ? String(propTournamentId) : id;
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [categories, setCategories] = useState<TournamentCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedClubId, setSelectedClubId] = useState<number | undefined>(undefined);
  const [editingWeights, setEditingWeights] = useState<Record<number, number>>({});
  const [changeCategoryModalVisible, setChangeCategoryModalVisible] = useState(false);
  const [selectedParticipantForCategoryChange, setSelectedParticipantForCategoryChange] = useState<Participant | null>(null);
  const [unregisteringParticipantId, setUnregisteringParticipantId] = useState<number | null>(null);
  const [changeClubModalVisible, setChangeClubModalVisible] = useState(false);
  const [selectedParticipantForClubChange, setSelectedParticipantForClubChange] = useState<Participant | null>(null);
  const [allClubsForSelect, setAllClubsForSelect] = useState<Club[]>([]);
  const [loadingClubsForSelect, setLoadingClubsForSelect] = useState(false);
  const [createClubModalVisible, setCreateClubModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [clubForm] = Form.useForm();

  useEffect(() => {
    if (tournamentId) {
    fetchParticipants();
    fetchClubs();
      fetchCategories();
    }
  }, [tournamentId, selectedClubId]);

  const fetchParticipants = async () => {
    if (!tournamentId) return;

    try {
      setLoading(true);
      const params: any = {};
      if (selectedClubId) {
        params.clubId = selectedClubId;
      }

      const response: any = await apiService.get(
        `/tournaments/${tournamentId}/weigh-in/participants`,
        { params }
      );

      // Обрабатываем разные форматы ответа
      let participantsData: Participant[] = [];
      if (response?.data?.data) {
        participantsData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (response?.data?.success && response?.data?.data) {
        participantsData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response?.data)) {
        participantsData = response.data;
      } else if (Array.isArray(response)) {
        participantsData = response;
      }

      if (participantsData.length === 0) {
        logger.warn('No participants found for weigh-in');
      }
      
      setParticipants(participantsData);
    } catch (error: any) {
      logger.error('Error fetching participants:', error);
      message.error(error?.response?.data?.message || 'Не удалось загрузить участников');
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    if (!tournamentId) return;
    
    try {
      // Получаем всех участников для извлечения уникальных клубов
      const response: any = await apiService.get(
        `/tournaments/${tournamentId}/weigh-in/participants`
      );

      // Обрабатываем разные форматы ответа
      let participantsData: Participant[] = [];
      if (response?.data?.data) {
        participantsData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response?.data)) {
        participantsData = response.data;
      } else if (Array.isArray(response)) {
        participantsData = response;
      }
        
        // Извлекаем уникальные клубы из участников
        const clubsMap = new Map<number, Club>();
        participantsData.forEach((participant: Participant) => {
          const club = participant.participant?.club;
          if (club && club.id && club.name) {
            clubsMap.set(club.id, {
              id: club.id,
              name: club.name
            });
          }
        });
        
        const uniqueClubs = Array.from(clubsMap.values());
        setClubs(uniqueClubs);
    } catch (error: any) {
      logger.error('Error fetching clubs:', error);
    }
  };

  const fetchCategories = async () => {
    if (!tournamentId) return;
    
    try {
      const response: any = await apiService.get(`/tournaments/${tournamentId}/categories`);
      
      let categoriesData: TournamentCategory[] = [];
      if (response?.data?.data) {
        categoriesData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response?.data)) {
        categoriesData = response.data;
      } else if (Array.isArray(response)) {
        categoriesData = response;
      }
      
      setCategories(sortTournamentCategories(categoriesData));
    } catch (error: any) {
      logger.error('Error fetching categories:', error);
    }
  };

  const handleWeightChange = (participantId: number, weight: number | null) => {
    setEditingWeights({
      ...editingWeights,
      [participantId]: weight || 0
    });
  };

  const handleSaveWeight = async (participantId: number) => {
    const weight = editingWeights[participantId];
    if (!weight || weight <= 0) {
      message.error('Введите корректный вес');
      return;
    }

    if (!tournamentId) return;

    try {
      await apiService.put(
        `/tournaments/${tournamentId}/weigh-in/participants/${participantId}/weight`,
        { weight }
      );

      message.success('Вес успешно обновлен');
      
      // Обновляем вес в локальном состоянии
      setParticipants(prev => prev.map(p => 
        p.participant.id === participantId
          ? { ...p, participant: { ...p.participant, weight } }
          : p
      ));

      // Удаляем из редактируемых
      const newEditing = { ...editingWeights };
      delete newEditing[participantId];
      setEditingWeights(newEditing);
    } catch (error: any) {
      logger.error('Error updating weight:', error);
      message.error(error?.response?.data?.message || 'Не удалось обновить вес');
    }
  };

  const handleUnregisterParticipant = async (record: Participant) => {
    const participantId = record.participant.id;
    const name = getFullName(record);
    Modal.confirm({
      title: 'Снять участника с турнира',
      content: (
        <span>
          Снять <strong>{name}</strong> с турнира? Участник будет удалён из списка, место вернётся в пакет подписки организатора.
        </span>
      ),
      okText: 'Снять с турнира',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        if (!tournamentId) return;
        try {
          setUnregisteringParticipantId(participantId);
          await TournamentService.unregisterParticipant(Number(tournamentId), participantId);
          message.success(`Участник ${name} снят с турнира. Место возвращено в подписку.`);
          await fetchParticipants();
        } catch (error: any) {
          logger.error('Error unregistering participant:', error);
          message.error(error.response?.data?.message || 'Не удалось снять участника с турнира');
        } finally {
          setUnregisteringParticipantId(null);
        }
      }
    });
  };

  const fetchAllClubsForSelect = async () => {
    try {
      setLoadingClubsForSelect(true);
      const response = await ClubService.getClubs({ limit: 1000 });
      const rows = response?.data && typeof response.data === 'object' && 'rows' in response.data
        ? (response.data as { rows: Club[] }).rows
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setAllClubsForSelect(rows);
    } catch (error: any) {
      logger.error('Error fetching clubs for select:', error);
      message.error('Не удалось загрузить список клубов');
    } finally {
      setLoadingClubsForSelect(false);
    }
  };

  const handleChangeClub = (record: Participant) => {
    setSelectedParticipantForClubChange(record);
    clubForm.setFieldsValue({ clubId: record.participant.clubId ?? record.participant.club?.id });
    setChangeClubModalVisible(true);
    fetchAllClubsForSelect();
  };

  const handleClubChangeSubmit = async () => {
    try {
      const values = await clubForm.validateFields();
      const { clubId } = values;
      if (!selectedParticipantForClubChange || !tournamentId) return;

      const participantId = selectedParticipantForClubChange.participant.id;
      if (clubId === selectedParticipantForClubChange.participant.clubId) {
        message.info('Клуб не изменился');
        setChangeClubModalVisible(false);
        return;
      }

      await apiService.put(
        `/tournaments/${tournamentId}/weigh-in/participants/${participantId}/club`,
        { clubId }
      );
      message.success('Клуб участника успешно обновлён');
      setChangeClubModalVisible(false);
      setSelectedParticipantForClubChange(null);
      clubForm.resetFields();
      await fetchParticipants();
      await fetchClubs();
    } catch (error: any) {
      logger.error('Error changing participant club:', error);
      message.error(error?.response?.data?.message || 'Ошибка при смене клуба');
    }
  };

  const handleChangeCategory = (record: Participant) => {
    setSelectedParticipantForCategoryChange(record);
    // В Select value — это tournament_categories.id; участник приходит с category.id = categories.id
    const tcId = record.category?.id != null
      ? categories.find(c => (c.categoryId ?? c.weightCategory?.id) === record.category?.id)?.id
      : undefined;
    form.setFieldsValue({ categoryId: tcId ?? record.category?.id });
    setChangeCategoryModalVisible(true);
  };

  const handleCategoryChangeSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { categoryId } = values;
      
      if (!selectedParticipantForCategoryChange || !tournamentId) return;

      const participantId = selectedParticipantForCategoryChange.participant.id;
      
      const currentTcId = selectedParticipantForCategoryChange.category?.id != null
        ? categories.find(c => (c.categoryId ?? c.weightCategory?.id) === selectedParticipantForCategoryChange.category?.id)?.id
        : undefined;
      if (categoryId === currentTcId) {
        message.info('Категория не изменилась');
        setChangeCategoryModalVisible(false);
        return;
      }

      const result = await TournamentService.updateParticipantCategory(Number(tournamentId), participantId, categoryId);
      if (result.categoriesNeedingReform > 0) {
        message.success(
          `Категория участника успешно обновлена. В ${result.categoriesNeedingReform} категории(ях) нужно переформировать сетки на странице жеребьёвки.`
        );
      } else {
        message.success('Категория участника успешно обновлена');
      }
      setChangeCategoryModalVisible(false);
      setSelectedParticipantForCategoryChange(null);
      form.resetFields();
      fetchParticipants(); // Обновляем список участников
    } catch (error: any) {
      logger.error('Error changing participant category:', error);
      message.error(error?.response?.data?.message || 'Ошибка при изменении категории участника');
    }
  };

  const getFullName = (record: Participant) =>
    `${record.participant.lastName} ${record.participant.firstName} ${record.participant.middleName || ''}`.trim();

  const columns: ColumnsType<Participant> = [
    {
      title: 'ФИО',
      key: 'name',
      render: (_, record) => (
        <Text strong>{getFullName(record)}</Text>
      ),
      width: 200,
      fixed: 'left',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Поиск по ФИО"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
            allowClear
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Поиск
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Сброс
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        const name = getFullName(record).toLowerCase();
        return name.includes(String(value).toLowerCase());
      }
    },
    {
      title: 'Клуб',
      key: 'club',
      render: (_, record) => (
        <Tag icon={<TeamOutlined />} color="blue">
          {record.participant.club?.name || '-'}
        </Tag>
      ),
      width: 150,
      filters: clubs.map((club) => ({ text: club.name, value: club.id })),
      onFilter: (value, record) => record.participant.club?.id === value
    },
    {
      title: 'Категория',
      key: 'category',
      render: (_, record) => (
        <Tag icon={<TrophyOutlined />} color="purple">
          {record.category?.name || '-'}
        </Tag>
      ),
      width: 150,
      filters: [...new Set(participants.map((p) => p.category?.name).filter(Boolean))].map((name) => ({
        text: name as string,
        value: name as string
      })),
      onFilter: (value, record) => (record.category?.name || '-') === value
    },
    {
      title: 'Текущий вес (кг)',
      key: 'currentWeight',
      render: (_, record) => (
        record.participant.weight ? (
          <Badge count={record.participant.weight} showZero={false}>
            <Tag color="default">{record.participant.weight} кг</Tag>
          </Badge>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
      width: 140
    },
    {
      title: 'Новый вес (кг)',
      key: 'newWeight',
      render: (_, record) => {
        const participantId = record.participant.id;
        const isEditing = participantId in editingWeights;
        const currentWeight = editingWeights[participantId] ?? record.participant.weight ?? 0;
        const weightInRange = record.category?.weightFrom && record.category?.weightTo
          ? currentWeight >= record.category.weightFrom && currentWeight <= record.category.weightTo
          : true;

        return (
          <Space>
            <InputNumber
              min={0}
              max={200}
              step={0.1}
              precision={1}
              value={isEditing ? editingWeights[participantId] : currentWeight}
              onChange={(value) => handleWeightChange(participantId, value)}
              style={{ width: 120 }}
              status={!weightInRange && currentWeight > 0 ? 'error' : undefined}
            />
            {isEditing && (
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={() => handleSaveWeight(participantId)}
              >
                Сохранить
              </Button>
            )}
            {!weightInRange && currentWeight > 0 && (
              <Tag color="error">Вне диапазона</Tag>
            )}
          </Space>
        );
      },
      width: 250
    },
    {
      title: 'Диапазон веса категории',
      key: 'weightRange',
      render: (_, record) => {
        if (!record.category?.weightFrom || !record.category?.weightTo) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Tag color="cyan">
            {record.category.weightFrom} - {record.category.weightTo} кг
          </Tag>
        );
      },
      width: 200
    },
    {
      title: 'Действия',
      key: 'actions',
      fixed: 'right',
      width: 320,
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            icon={<TeamOutlined />}
            onClick={() => handleChangeClub(record)}
            size="small"
          >
            Сменить клуб
          </Button>
          <Button
            type="link"
            icon={<SwapOutlined />}
            onClick={() => handleChangeCategory(record)}
            size="small"
          >
            Сменить категорию
          </Button>
          <Button
            type="link"
            danger
            icon={<UserDeleteOutlined />}
            onClick={() => handleUnregisterParticipant(record)}
            loading={unregisteringParticipantId === record.participant.id}
            size="small"
          >
            Снять с турнира
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Заголовок */}
      <Card>
        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
          <Flex align="center" gap={12}>
            <DashboardOutlined className="text-2xl text-blue-600" />
            <Title level={2} style={{ margin: 0 }}>
              Взвешивание участников
            </Title>
          </Flex>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchParticipants}
            loading={loading}
          >
            Обновить
          </Button>
        </Flex>
      </Card>

      {/* Фильтры */}
      <Card>
        <Flex align="center" gap={16} wrap="wrap">
          <Text strong>Фильтр по клубу:</Text>
            <Select
              style={{ width: 300 }}
              placeholder="Все клубы"
              allowClear
              value={selectedClubId}
              onChange={(value) => setSelectedClubId(value)}
            showSearch
            filterOption={(input, option) => {
              const children = option?.children as any;
              const text = typeof children === 'string' ? children : String(children || '');
              return text.toLowerCase().includes(input.toLowerCase());
            }}
            >
              {clubs.map(club => (
                <Option key={club.id} value={club.id}>
                  {club.name}
                </Option>
              ))}
            </Select>
          {selectedClubId && (
            <Tag 
              closable 
              onClose={() => setSelectedClubId(undefined)}
              color="blue"
            >
              {clubs.find(c => c.id === selectedClubId)?.name}
            </Tag>
          )}
        </Flex>
      </Card>

      {/* Информационное сообщение */}
      {participants.length > 0 && (
        <Alert
          message="Инструкция"
          description="Введите новый вес участника в поле 'Новый вес'. Если вес находится вне диапазона категории, будет показано предупреждение. Нажмите 'Сохранить' для сохранения изменений."
          type="info"
          showIcon
          closable
        />
      )}

      {/* Таблица участников */}
      <Card>
        {participants.length === 0 && !loading ? (
          <Empty
            description="Участники не найдены"
            image={<DashboardOutlined className="text-6xl text-gray-300" />}
          />
        ) : (
        <Table
          columns={columns}
          dataSource={participants}
          rowKey={(record) => record.id}
          loading={loading}
            scroll={{ x: 1000 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
              showTotal: (total) => `Всего участников: ${total}`,
              pageSizeOptions: ['10', '20', '50', '100']
          }}
        />
        )}
      </Card>

      {/* Модальное окно смены категории */}
      <Modal
        title="Сменить категорию участника"
        open={changeCategoryModalVisible}
        onOk={handleCategoryChangeSubmit}
        onCancel={() => {
          setChangeCategoryModalVisible(false);
          setSelectedParticipantForCategoryChange(null);
          form.resetFields();
        }}
        okText="Сохранить"
        cancelText="Отмена"
        width={500}
        destroyOnClose
      >
        {selectedParticipantForCategoryChange && (
          <Form form={form} layout="vertical">
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong>Участник: </Typography.Text>
              <Typography.Text>
                {selectedParticipantForCategoryChange.participant.lastName}{' '}
                {selectedParticipantForCategoryChange.participant.firstName}{' '}
                {selectedParticipantForCategoryChange.participant.middleName || ''}
              </Typography.Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong>Текущая категория: </Typography.Text>
              <Tag color="blue">
                {selectedParticipantForCategoryChange.category?.name || 'Не указана'}
              </Tag>
    </div>
            <Form.Item
              name="categoryId"
              label="Новая категория"
              rules={[{ required: true, message: 'Выберите категорию' }]}
            >
              <Select placeholder="Выберите категорию">
                {sortTournamentCategories(categories).map(category => (
                  <Option key={category.id} value={category.id}>
                    {category.weightCategory?.name ?? category.name ?? `Категория ${category.id}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Модальное окно назначения/смены клуба */}
      <Modal
        title="Назначить клуб участнику"
        open={changeClubModalVisible}
        onOk={handleClubChangeSubmit}
        onCancel={() => {
          setChangeClubModalVisible(false);
          setSelectedParticipantForClubChange(null);
          clubForm.resetFields();
        }}
        okText="Сохранить"
        cancelText="Отмена"
        width={500}
        destroyOnClose
      >
        {selectedParticipantForClubChange && (
          <Form form={clubForm} layout="vertical">
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong>Участник: </Typography.Text>
              <Typography.Text>
                {getFullName(selectedParticipantForClubChange)}
              </Typography.Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong>Текущий клуб: </Typography.Text>
              <Tag color="blue">
                {selectedParticipantForClubChange.participant.club?.name || 'Не указан'}
              </Tag>
            </div>
            <Form.Item
              name="clubId"
              label="Клуб"
              rules={[{ required: true, message: 'Выберите клуб' }]}
            >
              <Select
                placeholder="Выберите клуб или создайте новый"
                showSearch
                loading={loadingClubsForSelect}
                filterOption={(input, option) => {
                  const label = option?.children;
                  const text = typeof label === 'string' ? label : String(label ?? '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div style={{ padding: 8, borderTop: '1px solid #f0f0f0' }}>
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        block
                        onClick={() => {
                          setChangeClubModalVisible(false);
                          setCreateClubModalVisible(true);
                        }}
                      >
                        Создать новый клуб
                      </Button>
                    </div>
                  </>
                )}
              >
                {allClubsForSelect.map((club) => (
                  <Option key={club.id} value={club.id}>
                    {club.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        )}
      </Modal>

      <CreateClubModal
        visible={createClubModalVisible}
        club={null}
        onCancel={() => setCreateClubModalVisible(false)}
        onSuccess={() => {
          setCreateClubModalVisible(false);
          fetchAllClubsForSelect();
          setChangeClubModalVisible(true);
        }}
      />
    </Space>
  );
};

export default WeighInPage;

